import React from 'react';

import {
  InformationPage,
  OrganizationDetailsPage,
  SubdivisionDetailsPage,
  SubdivisionsPage,
  orgDetailsChildren,
  rolesChildren,
  subDetailsChildren,
} from '@gasbi/shared/web/Organization';
import {
  EmployeeDetails,
  MyOrganizationEmployees,
} from '@gasbi/shared/web/RbacOrganization';
import { Outlet } from 'react-router-dom';

import { UserPermissions } from '@/features/permissions';
import { Pages } from '@/pages';
import OrganizationApiContextWrapper from '@/providers/OrganizationApiContextWrapper';
import RbacOrganizationApiWrapper from '@/providers/RbacOrganizationApiWrapper';
import type { AppRouteObject } from '@/routeTypes';

const MyOrganization = React.lazy(() => import('@/pages/MyOrganization'));

// The legacy roles pages list roles over roles/query, which Households Billing guards with RoleAndPermission.Read;
// without it the page would only get a 404 (GSB-8308). Roles are administered in Distribution Admin.
const rolesRead = UserPermissions.OrganizationsRoleAndPermissionRead;
const OrganizationInformation = React.lazy(
  () => import('@/pages/OrganizationInformation'),
);
const SubdivisionEmployeeDetails = React.lazy(
  () => import('@/pages/SubdivisionEmployeeDetails'),
);

/**
 * The organization area over BMS (GSB-8317, PO 2026-10-26: «як у Supply»), as in BMS web. The current organization
 * is the shared RBAC page with BMS's tabs; the ГРМ card (on «Інформація», read-only, PO 2026-10-27), structural
 * subdivisions and roles stay on the legacy @gasbi/shared/web/Organization pages. There is no head office view any
 * more: it is Distribution Admin's.
 */
export const organizationRoutes: AppRouteObject[] = [
  {
    element: (
      <RbacOrganizationApiWrapper>
        <OrganizationApiContextWrapper>
          <MyOrganization />
        </OrganizationApiContextWrapper>
      </RbacOrganizationApiWrapper>
    ),
    children: [
      {
        element: <OrganizationInformation />,
        children: [{ index: true, element: <InformationPage /> }],
      },
      { path: Pages.EMPLOYEES, element: <MyOrganizationEmployees /> },
      { path: Pages.SUBDIVISIONS, element: <SubdivisionsPage /> },
    ],
  },
  {
    path: `${Pages.EMPLOYEES}/:employeeId`,
    element: (
      <RbacOrganizationApiWrapper>
        <EmployeeDetails />
      </RbacOrganizationApiWrapper>
    ),
  },
  {
    element: (
      <OrganizationApiContextWrapper>
        <RbacOrganizationApiWrapper>
          <Outlet />
        </RbacOrganizationApiWrapper>
      </OrganizationApiContextWrapper>
    ),
    children: [
      {
        path: `${Pages.SUBDIVISIONS}/:subId`,
        element: <SubdivisionDetailsPage />,
        children: subDetailsChildren,
      },
      {
        path: `${Pages.SUBDIVISIONS}/:subId/${Pages.EMPLOYEES}/:employeeId`,
        element: <SubdivisionEmployeeDetails />,
      },
      {
        path: Pages.ROLES,
        element: <Outlet />,
        children: rolesChildren(false),
        handle: { permissions: rolesRead },
      },
      {
        path: `${Pages.ORGANIZATIONS}/:orgId`,
        element: <OrganizationDetailsPage />,
        children: orgDetailsChildren(),
        handle: { keepAlive: true },
      },
      {
        path: `${Pages.ORGANIZATIONS}/:orgId/${Pages.SUBDIVISIONS}/:subId`,
        element: <SubdivisionDetailsPage />,
        children: subDetailsChildren,
      },
      {
        path: `${Pages.ORGANIZATIONS}/:orgId/${Pages.SUBDIVISIONS}/:subId/${Pages.EMPLOYEES}/:employeeId`,
        element: <SubdivisionEmployeeDetails />,
      },
      {
        path: `${Pages.ORGANIZATIONS}/:orgId/${Pages.ROLES}`,
        element: <Outlet />,
        children: rolesChildren(false),
        handle: { permissions: rolesRead },
      },
    ],
  },
];
